import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UserCog, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ProfileSettingsPage() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/profile", {
        name: name !== user.name ? name : undefined,
        email: email !== user.email ? email : undefined,
      });
      setUser(data);
      if (data.email_pending) {
        toast.success(t("Doğrulama bağlantısı yeni e-posta adresinize gönderildi. Adres doğrulanana kadar mevcut e-postanızla giriş yapmaya devam edebilirsiniz."));
      } else {
        toast.success("Profil güncellendi");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== newPassword2) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    setSavingPassword(true);
    try {
      const { data } = await api.patch("/profile", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(data);
      setCurrentPassword("");
      setNewPassword("");
      setNewPassword2("");
      toast.success("Şifreniz güncellendi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="fade-up max-w-2xl" data-testid="profile-settings-page">
      <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">{t("Profil Ayarları")}</h1>
      <p className="mt-2 text-slate-500">{t("Hesap bilgilerinizi ve şifrenizi güncelleyin.")}</p>

      <form onSubmit={saveProfile} className="mt-10 border border-slate-200 bg-white rounded-md p-8 space-y-5"
        data-testid="profile-form">
        <div className="flex items-center gap-3">
          <UserCog className="h-5 w-5 text-blue-600" />
          <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Hesap Bilgileri")}</h2>
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-name">{t("Ad Soyad")}</Label>
          <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="profile-name-input" className="focus-visible:ring-blue-600" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-email">{t("E-posta")}</Label>
          <Input id="profile-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="profile-email-input" className="focus-visible:ring-blue-600" />
          <p className="text-xs text-slate-400">{t("E-posta adresinizi buradan güncelleyebilirsiniz.")}</p>
          {user?.pending_email && (
            <p className="text-xs font-medium text-amber-600" data-testid="pending-email-note">
              {t("Doğrulama bekleniyor:")} {user.pending_email}
            </p>
          )}
        </div>
        <Button type="submit" disabled={savingProfile} data-testid="profile-save-button"
          className="bg-blue-600 hover:bg-blue-700 text-white">
          {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Kaydet")}
        </Button>
      </form>

      <form onSubmit={savePassword} className="mt-6 border border-slate-200 bg-white rounded-md p-8 space-y-5"
        data-testid="password-form">
        <div className="flex items-center gap-3">
          <KeyRound className="h-5 w-5 text-blue-600" />
          <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Şifre Değiştir")}</h2>
        </div>
        <div className="space-y-2">
          <Label htmlFor="current-password">{t("Mevcut Şifre")}</Label>
          <Input id="current-password" type="password" required value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            data-testid="current-password-input" className="focus-visible:ring-blue-600" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">{t("Yeni Şifre")}</Label>
            <Input id="new-password" type="password" required minLength={8} value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input" className="focus-visible:ring-blue-600" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password2">{t("Yeni Şifre (Tekrar)")}</Label>
            <Input id="new-password2" type="password" required minLength={8} value={newPassword2}
              onChange={(e) => setNewPassword2(e.target.value)}
              data-testid="new-password2-input" className="focus-visible:ring-blue-600" />
          </div>
        </div>
        <Button type="submit" disabled={savingPassword} data-testid="password-save-button"
          className="bg-blue-600 hover:bg-blue-700 text-white">
          {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Şifreyi Güncelle")}
        </Button>
      </form>
    </div>
  );
}
